import React from 'react';
import { Helmet } from 'react-helmet';
import { useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';

import Main from '../../layouts/Main';
import Container from '../../components/Container';
import { Content, Footer, Headline } from './components';

const Faq = () => {
  const theme = useTheme();

  const faqPageSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      { "@type": "Question", "name": "What areas do you serve?", "acceptedAnswer": { "@type": "Answer", "text": "We proudly serve cities in Wayne and Johnston County, NC, including Goldsboro, Smithfield, Clayton, and Selma." }},
      { "@type": "Question", "name": "Do you offer free estimates?", "acceptedAnswer": { "@type": "Answer", "text": "Yes, we provide free estimates for all landscaping services. Contact us to schedule an appointment in Goldsboro or any nearby area." }},
      { "@type": "Question", "name": "What services do you provide?", "acceptedAnswer": { "@type": "Answer", "text": "We offer lawn care, landscaping, hardscaping, tree trimming, and seasonal clean-up services in Wayne and Johnston County." }},
      { "@type": "Question", "name": "Are your services customizable?", "acceptedAnswer": { "@type": "Answer", "text": "Absolutely! We tailor our services to meet your specific needs, whether you need regular lawn maintenance or a one-time landscaping project." }},
      { "@type": "Question", "name": "Do you provide emergency storm clean-up?", "acceptedAnswer": { "@type": "Answer", "text": "Yes, we offer storm clean-up services in cities like Goldsboro and Clayton to quickly restore your yard to its best condition." }},
      { "@type": "Question", "name": "How do I schedule a service?", "acceptedAnswer": { "@type": "Answer", "text": "You can schedule a service by calling us or using our online booking form. We serve clients in Goldsboro, Smithfield, and surrounding areas." }},
      { "@type": "Question", "name": "What payment methods do you accept?", "acceptedAnswer": { "@type": "Answer", "text": "We accept cash, checks, and major credit cards for your convenience." }},
      { "@type": "Question", "name": "Do you offer recurring service plans?", "acceptedAnswer": { "@type": "Answer", "text": "Yes, we offer weekly, bi-weekly, and monthly plans for lawn care and landscaping in Johnston and Wayne County." }},
      { "@type": "Question", "name": "Can I reschedule or cancel my appointment?", "acceptedAnswer": { "@type": "Answer", "text": "Yes, please contact us at least 24 hours in advance to reschedule or cancel your appointment." }},
      { "@type": "Question", "name": "Do you offer any discounts?", "acceptedAnswer": { "@type": "Answer", "text": "We provide discounts for senior citizens, military personnel, and first-time customers in Goldsboro and nearby areas." }},
      { "@type": "Question", "name": "How often should I mow my lawn?", "acceptedAnswer": { "@type": "Answer", "text": "We recommend mowing your lawn every 1-2 weeks depending on the season and growth rate." }},
      { "@type": "Question", "name": "Do you offer weed control services?", "acceptedAnswer": { "@type": "Answer", "text": "Yes, we provide effective weed control services to keep your lawn looking pristine throughout Wayne and Johnston County." }},
      { "@type": "Question", "name": "Can you help with irrigation system installation?", "acceptedAnswer": { "@type": "Answer", "text": "Yes, we specialize in installing and maintaining irrigation systems in cities like Clayton and Selma." }},
      { "@type": "Question", "name": "What is included in seasonal clean-up?", "acceptedAnswer": { "@type": "Answer", "text": "Seasonal clean-up includes leaf removal, trimming, and preparing your lawn for the upcoming season." }},
      { "@type": "Question", "name": "Do you provide pest control for lawns?", "acceptedAnswer": { "@type": "Answer", "text": "Yes, we offer pest control solutions to protect your lawn from common pests in Goldsboro and surrounding areas." }}
    ]
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://wrightbrotherslawncare.com/" },
      { "@type": "ListItem", "position": 2, "name": "FAQ", "item": "https://wrightbrotherslawncare.com/faq" }
    ]
  };

  return (
    <Main>
      <Helmet>
        <title>Frequently Asked Questions | Wright Brothers Lawn Care - Wayne &amp; Johnston County, NC</title>
        <meta name="description" content="Find answers to frequently asked questions about Wright Brothers Lawn Care services, scheduling, payments, lawn maintenance, and more in Wayne and Johnston County, NC." />
        <meta name="keywords" content="lawn care FAQ, landscaping questions, Wayne County NC, Johnston County NC, lawn maintenance questions, free estimates, scheduling lawn care, Wright Brothers Lawn Care" />
        <link rel="canonical" href="https://wrightbrotherslawncare.com/faq" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://wrightbrotherslawncare.com/faq" />
        <meta property="og:title" content="Frequently Asked Questions | Wright Brothers Lawn Care" />
        <meta property="og:description" content="Get answers to your lawn care and landscaping questions. Learn about our services, scheduling, payments, and more in Wayne and Johnston County, NC." />
        <meta property="og:site_name" content="Wright Brothers Lawn Care" />
        <meta property="og:locale" content="en_US" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Frequently Asked Questions | Wright Brothers Lawn Care" />
        <meta name="twitter:description" content="Get answers to your lawn care and landscaping questions in Wayne and Johnston County, NC." />
        <meta name="robots" content="index, follow, max-image-preview:large" />
        <script type="application/ld+json">
          {JSON.stringify(faqPageSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      </Helmet>
      <Box>
        <Box
          sx={{
            backgroundColor: theme.palette.alternate.main,
            backgroundImage: `linear-gradient(120deg, ${theme.palette.background.paper} 0%, ${theme.palette.alternate.main} 100%)`,
            marginTop: -13,
            paddingTop: 13,
          }}
        >
          <Container>
            <Headline />
          </Container>
        </Box>
        <Container maxWidth={800}>
          <Content />
        </Container>
        <Box bgcolor={theme.palette.alternate.main}>
          <Container>
            <Footer />
          </Container>
        </Box>
      </Box>
    </Main>
  );
};

export default Faq;
